"use client"

// Client glue for canonical appearance persistence (Phase 3 mode + Phase 4 accent).
//
// Two distinct precedences, kept separate on purpose:
//   1. FIRST PAINT (before the authenticated preference is known) — mode: `?sm_theme` >
//      `sm_appearance` cookie > next-themes localStorage > default; accent: the `sm_accent` cookie
//      read server-side by the Careers layout (data-accent in the initial HTML). A STALE cache may
//      decide frame one; that is acceptable.
//   2. CANONICAL PERSISTENCE (authenticated) — `identity_users.appearance` ({mode, accent}) is the
//      cross-device source of truth. Once known, the device reconciles to it after mount.
//
// next-themes stays the single MODE authority (the class on <html> + localStorage['theme']). ACCENT
// has no next-themes equivalent, so it is applied as `data-accent` on the product boundary element
// ([data-product="…"]); which accent a product may show is gated by product policy (accents.ts),
// so other products never acquire personalization.

import { useEffect, useRef } from "react"
import { useTheme } from "next-themes"
import { writeAppearanceCookie, writeAccentCookie, shouldApplyServerAppearance } from "@/lib/platform/themeSync"
import { resolveAccent, isAccentValue } from "@/lib/platform/accents"

// Set true the moment the user makes an explicit choice in THIS session, so an in-flight reconcile
// (a fetch started at mount reading an OLD server value) can't land afterward and clobber the fresh
// choice — which was itself already written to the server. Tracked per dimension (mode / accent)
// because the two persist independently.
let sessionModeChosen = false
let sessionAccentChosen = false

/** The product boundary element (carries data-product + data-accent), or null off a product surface. */
function productEl(): Element | null {
  if (typeof document === "undefined") return null
  return document.querySelector("[data-product]")
}

/** Apply (or clear) the live accent on the product boundary. Called with the RESOLVED id — i.e. the
 *  product policy has already decided whether this accent is allowed here. */
function stampAccent(appliedId: string | null): void {
  const el = productEl()
  if (!el) return
  if (appliedId) el.setAttribute("data-accent", appliedId)
  else el.removeAttribute("data-accent")
}

/** Persist an explicit MODE choice: per-device cookie + durable server row. Call right after
 *  next-themes `setTheme(mode)`. Non-blocking, non-fatal; signed-out callers keep a local-only pref. */
export function persistAppearanceMode(mode: string): void {
  sessionModeChosen = true
  writeAppearanceCookie(mode)
  void fetch("/api/platform/appearance", {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ mode }),
  }).catch(() => { /* cookie + localStorage still hold the choice locally */ })
}

/** Persist an explicit ACCENT choice: live DOM stamp (gated by the current product's policy) +
 *  per-device cookie (raw id) + durable server row. `null` clears back to the default orange. */
export function persistAppearanceAccent(accentId: string | null): void {
  sessionAccentChosen = true
  writeAccentCookie(accentId)
  stampAccent(resolveAccent(productEl()?.getAttribute("data-product"), accentId))
  void fetch("/api/platform/appearance", {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ accent: accentId }),
  }).catch(() => { /* cookie still holds the choice locally for this device */ })
}

/** Reconcile THIS device to the user's canonical appearance after mount (ONE fetch, both dimensions).
 *
 *  Always reads the server row (not only on a cookie-less device): the canonical preference must win
 *  over a stale device cache, else a change on another device would never reach this one. mode rides
 *  next-themes; accent is seeded into its cookie (raw id, so any product resolves it correctly) and,
 *  on a personalization product, applied live via data-accent. Each dimension is skipped when the
 *  user changed it THIS session (that newer choice already wrote the server). Signed-out / no stored
 *  value → keep the device's first-paint value; persist nothing. */
export function AppearanceSync(): null {
  const { setTheme } = useTheme()
  const ran = useRef(false)
  useEffect(() => {
    if (ran.current) return
    ran.current = true
    let active = true
    ;(async () => {
      try {
        const r = await fetch("/api/platform/appearance", { headers: { accept: "application/json" } })
        const j = (await r.json().catch(() => null)) as { appearance?: { mode?: unknown; accent?: unknown } | null } | null
        if (!active) return
        // MODE (canonical precedence, pure + unit-tested).
        const decision = shouldApplyServerAppearance(j?.appearance?.mode, sessionModeChosen)
        if (decision.apply) {
          writeAppearanceCookie(decision.mode)
          setTheme(decision.mode)
        }
        // ACCENT. Seed the device cookie from the canonical raw choice and, on a personalization
        // product, apply it live. null is a real value (= default orange): clear the cookie + stamp.
        if (!sessionAccentChosen) {
          const rawAccent = j?.appearance?.accent
          if (isAccentValue(rawAccent)) {
            writeAccentCookie(rawAccent)
            stampAccent(resolveAccent(productEl()?.getAttribute("data-product"), rawAccent))
          }
        }
      } catch { /* non-fatal: keep the device value */ }
    })()
    return () => { active = false }
  }, [setTheme])
  return null
}
